import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { 
  Plus, 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  ArrowRight,
  Users,
  BookOpen,
  Building,
  GraduationCap,
  BarChart,
  Filter,
  Search,
  FileText,
  CheckCircle,
  MapPin,
  ChevronRight,
  ArrowUpRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { format, addDays, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Class, ClassSession, ClassStatus, ClassSessionStatus } from '@/types';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/StatusBadge';

interface ClassWithSessions extends Class {
  courses?: {
    name: string;
  };
  sessions?: ClassSession[];
  customer_relationship?: {
    name: string;
  };
}

interface SessionWithClass extends ClassSession {
  classes?: {
    id: string;
    status: ClassStatus;
    courses?: {
      name: string;
    };
    customer_relationship?: {
      name: string;
    };
  };
  instructors?: {
    first_name: string;
    last_name: string;
  };
}

const ClassesDashboard = () => {
  const navigate = useNavigate();
  const [classes, setClasses] = useState<ClassWithSessions[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<SessionWithClass[]>([]);
  const [sessionsCalendar, setSessionsCalendar] = useState<SessionWithClass[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [stats, setStats] = useState({
    totalClasses: 0,
    activeClasses: 0,
    upcomingSessions: 0,
    completedSessions: 0,
    totalParticipants: 0,
    certificatesIssued: 0,
    completionRate: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchClasses(),
        fetchUpcomingSessions(),
        fetchSessionsCalendar(),
        fetchStats(),
        fetchParticipantStats()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const { data, error } = await supabase
        .from('classes')
        .select(`
          *,
          id,
          status,
          location_address,
          courses(name),
          subscriptions:subscription_id(
            customer_relationship:customer_relationship_id(
              customer:customer_id(name)
            )
          )
        `)
        .eq('status', 'scheduled')
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      
      // Transformar os dados para o formato esperado
      const classesWithTenant = data.map(cls => {
        // Access the nested structure correctly
        const customerName = (cls.subscriptions as any)?.customer_relationship?.customer?.name || 'Tenant Desconhecido';
        // Remove the original subscriptions data to avoid type conflicts if necessary, or adjust interface
        const { subscriptions, ...restOfCls } = cls; 
        return {
          ...restOfCls,
          // Map the name to the expected structure for the interface
          customer_relationship: { name: customerName },
        };
      });
      
      setClasses(classesWithTenant as any); // Cast to any temporarily if interface doesn't match exactly
    } catch (error) {
      console.error('Erro ao buscar classes:', error);
      throw error;
    }
  };

  const fetchUpcomingSessions = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data, error } = await supabase
        .from('class_sessions')
        .select(`
          *,
          instructors(first_name, last_name),
          classes(
            id, 
            status,
            courses(name),
            subscriptions:subscription_id(
              customer_relationship:customer_relationship_id(
                customer:customer_id(name)
              )
            )
          )
        `)
        .gte('start_date', today.toISOString())
        .order('start_date', { ascending: true })
        .limit(5);

      if (error) throw error;
      
      // Transformar os dados para o formato esperado
      const sessionsWithTenant = data.map(session => {
        const cls = session.classes as any; // Cast classes to any for easier access
        const customerName = cls?.subscriptions?.customer_relationship?.customer?.name || 'Tenant Desconhecido';
        // Ensure we don't overwrite the classes object if it's null/undefined
        const updatedClasses = cls ? {
          ...cls, 
          // Remove original subscriptions from classes object if needed
          subscriptions: undefined, 
          customer_relationship: { name: customerName }
        } : null;

        return {
          ...session,
          classes: updatedClasses
        };
      });
      
      setUpcomingSessions(sessionsWithTenant as any); // Cast to any temporarily
    } catch (error) {
      console.error('Erro ao buscar próximas sessões:', error);
      throw error;
    }
  };

  const fetchSessionsCalendar = async () => {
    try {
      const startDate = new Date();
      startDate.setDate(1); // Primeiro dia do mês atual
      
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + 1);
      endDate.setDate(0); // Último dia do mês atual
      
      const { data, error } = await supabase
        .from('class_sessions')
        .select(`
          *,
          instructors(first_name, last_name),
          classes(
            id, 
            status,
            courses(name),
            subscriptions:subscription_id(
              customer_relationship:customer_relationship_id(
                customer:customer_id(name)
              )
            )
          )
        `)
        .gte('start_date', startDate.toISOString())
        .lte('start_date', endDate.toISOString())
        .order('start_date', { ascending: true });

      if (error) throw error;
      
      // Transformar os dados para o formato esperado
      const sessionsWithTenant = data.map(session => {
        const cls = session.classes as any; // Cast classes to any for easier access
        const customerName = cls?.subscriptions?.customer_relationship?.customer?.name || 'Tenant Desconhecido';
         // Ensure we don't overwrite the classes object if it's null/undefined
        const updatedClasses = cls ? {
          ...cls,
          // Remove original subscriptions from classes object if needed
          subscriptions: undefined, 
          customer_relationship: { name: customerName }
        } : null;

        return {
          ...session,
          classes: updatedClasses
        };
      });
      
      setSessionsCalendar(sessionsWithTenant as any); // Cast to any temporarily
    } catch (error) {
      console.error('Erro ao buscar sessões para o calendário:', error);
      throw error;
    }
  };

  const fetchStats = async () => {
    try {
      // Total de classes
      const { count: totalClasses, error: errorTotal } = await supabase
        .from('classes')
        .select('*', { count: 'exact', head: true });
      
      if (errorTotal) throw errorTotal;
      
      // Classes ativas
      const { count: activeClasses, error: errorActive } = await supabase
        .from('classes')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'scheduled');
      
      if (errorActive) throw errorActive;
      
      // Próximas sessões
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { count: upcomingSessions, error: errorUpcoming } = await supabase
        .from('class_sessions')
        .select('*', { count: 'exact', head: true })
        .gte('start_date', today.toISOString());
      
      if (errorUpcoming) throw errorUpcoming;
      
      // Sessões completadas
      const { count: completedSessions, error: errorCompleted } = await supabase
        .from('class_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'confirmed');
      
      if (errorCompleted) throw errorCompleted;
      
      setStats(prevStats => ({
        ...prevStats,
        totalClasses: totalClasses || 0,
        activeClasses: activeClasses || 0,
        upcomingSessions: upcomingSessions || 0,
        completedSessions: completedSessions || 0
      }));
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      throw error;
    }
  };

  const fetchParticipantStats = async () => {
    try {
      // Total de participantes
      const { count: totalParticipants, error: errorParticipants } = await supabase
        .from('class_participants')
        .select('*', { count: 'exact', head: true });
      
      if (errorParticipants) throw errorParticipants;
      
      // Certificados emitidos
      const { count: certificatesIssued, error: errorCertificates } = await supabase
        .from('class_certificates')
        .select('*', { count: 'exact', head: true });
      
      if (errorCertificates) {
        if (errorCertificates.code === '42P01') {
          console.warn('Tabela "class_certificates" não encontrada ou sem permissão de acesso. Estatísticas de certificado serão 0.');
          setStats(prevStats => ({
            ...prevStats,
            totalParticipants: totalParticipants || 0,
            certificatesIssued: 0,
            completionRate: 0
          }));
        } else {
          throw errorCertificates;
        }
      } else {
        const completionRate = totalParticipants && certificatesIssued 
          ? Math.round((certificatesIssued / totalParticipants) * 100) 
          : 0;
        
        setStats(prevStats => ({
          ...prevStats,
          totalParticipants: totalParticipants || 0,
          certificatesIssued: certificatesIssued || 0,
          completionRate: completionRate
        }));
      }
      
    } catch (error) {
      console.error('Erro ao buscar estatísticas de participantes:', error);
      setStats(prevStats => ({
        ...prevStats,
        totalParticipants: prevStats.totalParticipants || 0,
        certificatesIssued: 0,
        completionRate: 0
      }));
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const getSessionsForDate = (date: Date) => {
    return sessionsCalendar.filter(session => 
      isSameDay(new Date(session.start_date), date)
    );
  };

  const sessionsForSelectedDate = getSessionsForDate(selectedDate);

  return (
    <div className="w-full">
      {/* Cabeçalho moderno */}
      <div className="bg-white dark:bg-gray-950 rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16 border-2">
              <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                <GraduationCap className="h-8 w-8" />
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-extrabold">Dashboard de Formações</h1>
              </div>
              <p className="text-muted-foreground mt-1">
                Acompanhe métricas e desempenho de suas formações
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 md:mt-0">
            <Button
              onClick={() => navigate('/classes/new')}
              className="gap-2"
            >
              <Plus className="h-4 w-4" /> Nova Formação
            </Button>
          </div>
        </div>
      </div>

      {/* Seletor de período e filtros */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Período" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Esta Semana</SelectItem>
              <SelectItem value="month">Este Mês</SelectItem>
              <SelectItem value="quarter">Este Trimestre</SelectItem>
              <SelectItem value="year">Este Ano</SelectItem>
              <SelectItem value="all">Todo o Período</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-1">
            <Filter className="h-4 w-4" />
            Filtros
          </Button>
        </div>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Buscar formações..."
            className="pl-8"
          />
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="calendar">Calendário</TabsTrigger>
          <TabsTrigger value="performance">Desempenho</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          {/* Cards de métricas principais */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Total de Formações</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
                {loading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
            <div className="text-2xl font-extrabold">{stats.totalClasses}</div>
                )}
            <p className="text-xs text-muted-foreground">
              {stats.activeClasses} formações ativas
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Próximas Aulas</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
                {loading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
            <div className="text-2xl font-extrabold">{stats.upcomingSessions}</div>
                )}
            <p className="text-xs text-muted-foreground">
              Aulas agendadas
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-extrabold">Participantes</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
                {loading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-extrabold">{stats.totalParticipants}</div>
                )}
            <p className="text-xs text-muted-foreground">
                  {stats.certificatesIssued} certificados emitidos
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-extrabold">Taxa de Conclusão</CardTitle>
                <BarChart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
                {loading ? (
                  <Skeleton className="h-8 w-16" />
                ) : (
                  <div className="text-2xl font-extrabold">{stats.completionRate}%</div>
                )}
                <Progress value={stats.completionRate} className="h-2 mt-2" />
          </CardContent>
        </Card>
      </div>

          {/* Layout de duas colunas */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Coluna da esquerda (2/3) */}
            <div className="lg:col-span-2 space-y-6">
              {/* Próximas formações */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle>Formações Agendadas</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => navigate('/classes')}>
                      Ver todas
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-start gap-4 p-3 rounded-lg border">
                          <Skeleton className="h-12 w-12 rounded-full" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-5 w-1/3" />
                            <Skeleton className="h-4 w-1/4" />
                            <Skeleton className="h-4 w-1/2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : classes.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      Nenhuma formação agendada encontrada
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {classes.map((cls) => (
                        <div 
                          key={cls.id}
                          className="flex items-start gap-4 p-3 rounded-lg border hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => navigate(`/classes/${cls.id}`)}
                        >
                          <Avatar className="h-12 w-12">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              <BookOpen className="h-5 w-5" />
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h3 className="font-extrabold">{cls.courses?.name || "Curso não atribuído"}</h3>
                            </div>
                            <div className="mt-1 text-sm text-muted-foreground">
                              <div className="flex items-center mt-1">
                                <Building className="h-3.5 w-3.5 mr-1" />
                                <span>{cls.customer_relationship?.name || "Cliente não associado"}</span>
                              </div>
                              {cls.location_address && (
                                <div className="flex items-center mt-1">
                                  <MapPin className="h-3.5 w-3.5 mr-1" />
                                  <span>{cls.location_address}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Atividades recentes/histórico de aulas */}
              <Card>
          <CardHeader>
                  <CardTitle>Histórico de Aulas</CardTitle>
            <CardDescription>
                    Últimas aulas registradas no sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
                    <div className="h-[300px] flex items-center justify-center">
                      <Skeleton className="h-[250px] w-full" />
                    </div>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      [Gráfico de Histórico de Aulas]
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Coluna da direita (1/3) */}
            <div className="space-y-6">
              {/* Próximas sessões */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle>Próximas Aulas</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="flex items-start space-x-4 pb-4 border-b last:border-0 last:pb-0">
                          <Skeleton className="h-10 w-10" />
                          <div className="flex-1 space-y-2">
                            <Skeleton className="h-5 w-3/4" />
                            <Skeleton className="h-4 w-1/2" />
                          </div>
                        </div>
                      ))}
                    </div>
            ) : upcomingSessions.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                Nenhuma aula agendada
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingSessions.map((session) => (
                        <div key={session.id} className="flex items-start space-x-4 pb-4 border-b last:border-0 last:pb-0">
                          <div className="min-w-10 text-center">
                            <div className="text-xl font-extrabold">
                              {format(new Date(session.start_date), "dd", { locale: ptBR })}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {format(new Date(session.start_date), "MMM", { locale: ptBR })}
                            </div>
                    </div>
                          <div className="flex-1">
                            <p className="text-sm font-extrabold">
                        {session.classes?.courses?.name || 'Sem curso'}
                      </p>
                            <div className="flex items-center text-xs text-muted-foreground mt-1">
                              <Clock className="h-3 w-3 mr-1" />
                              <span>
                                {format(new Date(session.start_date), "HH:mm", { locale: ptBR })}
                                {session.end_date ? ` - ${format(new Date(session.end_date), "HH:mm", { locale: ptBR })}` : ''}
                              </span>
                            </div>
                            <div className="flex items-center text-xs text-muted-foreground mt-1">
                              <User className="h-3 w-3 mr-1" />
                              <span>
                          {session.instructors 
                            ? `${session.instructors.first_name} ${session.instructors.last_name}` 
                            : 'Sem instrutor'}
                        </span>
                      </div>
                          </div>
                          <StatusBadge status={session.status} context="session" />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
                <CardFooter className="pt-0">
                  <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => navigate('/classes')}>
                    <ArrowUpRight className="h-4 w-4" />
                    Ver Todas as Aulas
                  </Button>
                </CardFooter>
              </Card>

              {/* Status de certificações */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle>Certificações</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-4">
                      <Skeleton className="h-5 w-full" />
                      <Skeleton className="h-2 w-full" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Certificados Emitidos</div>
                        <div className="font-extrabold">{stats.certificatesIssued}</div>
                      </div>
                      <Progress value={70} className="h-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <div>Meta: {stats.totalParticipants}</div>
                        <div>{stats.completionRate}% concluído</div>
                      </div>
              </div>
            )}
          </CardContent>
                <CardFooter className="pt-0">
                  <Button variant="outline" size="sm" className="w-full gap-2">
                    <FileText className="h-4 w-4" />
                    Relatório de Certificações
                  </Button>
                </CardFooter>
        </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="calendar" className="mt-4">
          <Card>
          <CardHeader>
              <CardTitle>Calendário de Formações</CardTitle>
            <CardDescription>
                Visualize todas as sessões agendadas para o período
            </CardDescription>
          </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="col-span-1">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
                    className="border rounded-md p-3"
                    // Destacar dias com sessões
              modifiers={{
                      highlighted: sessionsCalendar.map(session => new Date(session.start_date))
              }}
              modifiersStyles={{
                      highlighted: { fontWeight: 'bold', backgroundColor: 'rgba(59, 130, 246, 0.1)' }
                    }}
                  />
                </div>
                <div className="col-span-1 md:col-span-2">
                  <h3 className="text-lg font-extrabold mb-4">
                Aulas em {format(selectedDate, 'dd/MM/yyyy', { locale: ptBR })}
              </h3>
              
                  {loading ? (
                    <div className="space-y-4">
                      {[1, 2, 3].map(i => (
                        <Skeleton key={i} className="h-24 w-full" />
                      ))}
                    </div>
                  ) : sessionsForSelectedDate.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <CalendarIcon className="h-12 w-12 mb-2 opacity-20" />
                      <p>Nenhuma aula agendada para esta data</p>
                </div>
              ) : (
                    <div className="space-y-4">
                  {sessionsForSelectedDate.map((session) => (
                        <div 
                          key={session.id} 
                          className="p-4 border rounded-md hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => navigate(`/classes/${session.class_id}`)}
                        >
                      <div className="flex justify-between items-start">
                        <div>
                              <h4 className="font-extrabold">
                            {session.classes?.courses?.name || 'Sem curso'}
                              </h4>
                              <div className="flex flex-col sm:flex-row sm:gap-4 mt-1">
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <Clock className="h-3.5 w-3.5 mr-1" />
                                  <span>
                                    {format(new Date(session.start_date), "HH:mm", { locale: ptBR })}
                                    {session.end_date ? ` - ${format(new Date(session.end_date), "HH:mm", { locale: ptBR })}` : ''}
                                  </span>
                                </div>
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <User className="h-3.5 w-3.5 mr-1" />
                                  <span>
                              {session.instructors 
                                ? `${session.instructors.first_name} ${session.instructors.last_name}` 
                                : 'Sem instrutor'}
                            </span>
                          </div>
                              </div>
                              {session.classes?.customer_relationship?.name && (
                                <div className="flex items-center text-sm text-muted-foreground mt-1">
                                  <Building className="h-3.5 w-3.5 mr-1" />
                                  <span>{session.classes.customer_relationship.name}</span>
                          </div>
                              )}
                        </div>
                        <div>
                          <StatusBadge status={session.status} context="session" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Desempenho de Formações</CardTitle>
              <CardDescription>
                Análise de desempenho por participação e avaliações
              </CardDescription>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Formações por Mês</CardTitle>
                    <CardDescription>
                      Número de formações realizadas por período
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      [Gráfico de Formações por Mês]
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Participação</CardTitle>
                    <CardDescription>
                      Taxa de participação nas formações
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      [Gráfico de Participação]
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Avaliações</CardTitle>
                    <CardDescription>
                      Média de avaliações por formação
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      [Gráfico de Avaliações]
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Conclusões</CardTitle>
                    <CardDescription>
                      Taxa de conclusão das formações
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                      [Gráfico de Conclusões]
            </div>
          </CardContent>
        </Card>
      </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ClassesDashboard; 